import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName, buildDVTSummaryLines } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

const NORMAL_STATUS = 'Normal - Compressible, patent';

const VEIN_LABELS = {
  internalJugularVein: 'Internal jugular vein',
  subclavianVein: 'Subclavian vein',
  axillaryVein: 'Axillary vein',
  brachialVeins: 'Brachial veins',
  basilicVein: 'Basilic vein',
  cephalicVein: 'Cephalic vein',
};

export function UpperExtremityDVTPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const ra = data.rightArm || {};
  const la = data.leftArm || {};

  const rightSummary = data.rightArmEvaluated ? buildDVTSummaryLines(VEIN_LABELS, ra, 'Right', 'upper') : null;
  const leftSummary = data.leftArmEvaluated ? buildDVTSummaryLines(VEIN_LABELS, la, 'Left', 'upper') : null;

  const isVeinAbnormal = (vein) => {
    const s = (vein && vein.status) || '';
    return s !== '' && s !== NORMAL_STATUS;
  };

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="UPPER EXTREMITY DVT ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>FINDINGS SUMMARY</Text>
              {rightSummary && (
                <>
                  <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginTop: 4 }]}>Right Arm:</Text>
                  {rightSummary.map((line, i) => (
                    <Text key={i} style={[commonStyles.infoBoxText, { color: line.tone === 'abnormal' ? COLORS.abnormal : line.tone === 'normal' ? COLORS.normal : COLORS.black }]}>{line.text}</Text>
                  ))}
                </>
              )}
              {leftSummary && (
                <>
                  <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginTop: 6 }]}>Left Arm:</Text>
                  {leftSummary.map((line, i) => (
                    <Text key={i} style={[commonStyles.infoBoxText, { color: line.tone === 'abnormal' ? COLORS.abnormal : line.tone === 'normal' ? COLORS.normal : COLORS.black }]}>{line.text}</Text>
                  ))}
                </>
              )}
              {!rightSummary && !leftSummary && (
                <Text style={commonStyles.infoBoxText}>No arms evaluated</Text>
              )}
            </View>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {data.rightArmEvaluated && (
              <>
                <PdfAccentSectionHeader title="RIGHT ARM" />
                {Object.entries(VEIN_LABELS).map(([key, label]) => {
                  const vein = ra[key] || {};
                  return (
                    <View key={key}>
                      <PdfLabelValue
                        label={`${label}:`}
                        value={vein.status || ''}
                        isAbnormal={isVeinAbnormal(vein)}
                      />
                      {vein.notes && (
                        <View style={{ paddingLeft: 10 }}>
                          <PdfLabelValue label="Notes:" value={vein.notes} isAbnormal />
                        </View>
                      )}
                    </View>
                  );
                })}
              </>
            )}

            {data.leftArmEvaluated && (
              <>
                <PdfAccentSectionHeader title="LEFT ARM" />
                {Object.entries(VEIN_LABELS).map(([key, label]) => {
                  const vein = la[key] || {};
                  return (
                    <View key={key}>
                      <PdfLabelValue
                        label={`${label}:`}
                        value={vein.status || ''}
                        isAbnormal={isVeinAbnormal(vein)}
                      />
                      {vein.notes && (
                        <View style={{ paddingLeft: 10 }}>
                          <PdfLabelValue label="Notes:" value={vein.notes} isAbnormal />
                        </View>
                      )}
                    </View>
                  );
                })}
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
